// Utility per validare le fasce orarie contro i parametri contrattuali

export interface ShiftValidationIssue {
  type: 'ERROR' | 'WARNING';
  message: string;
  field?: string;
  expected?: number;
  actual?: number;
}

export interface ShiftValidationResult {
  isValid: boolean;
  weeklyHoursExpected: number;
  weeklyDaysExpected: number;
  dailyHoursExpected: number;
  totalHoursFromSlots: number;
  difference: number;
  issues: ShiftValidationIssue[];
}

export interface WorkShiftSlot {
  id?: number;
  employee_id: number;
  day_of_week: number;
  slot_name: string;
  start_time: string;
  end_time: string;
  break_duration_minutes: number;
  is_active: boolean;
}

export interface EmployeeContractData {
  ore_settimanali: number;
  giorni_settimanali: number;
}

/**
 * Calcola le ore da una fascia oraria
 */
export function calculateSlotHours(slot: WorkShiftSlot): number {
  const [startHour, startMin] = slot.start_time.split(':').map(Number);
  const [endHour, endMin] = slot.end_time.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  
  const totalMinutes = endMinutes - startMinutes - slot.break_duration_minutes;
  return totalMinutes / 60;
}

/**
 * Valida le fasce orarie contro i parametri contrattuali
 */
export function validateShifts(
  slots: WorkShiftSlot[],
  contract: EmployeeContractData
): ShiftValidationResult {
  const result: ShiftValidationResult = {
    isValid: true,
    weeklyHoursExpected: contract.ore_settimanali,
    weeklyDaysExpected: contract.giorni_settimanali,
    dailyHoursExpected: contract.ore_settimanali / contract.giorni_settimanali,
    totalHoursFromSlots: 0,
    difference: 0,
    issues: [],
  };

  // Raggruppa slot per giorno
  const slotsByDay: { [day: number]: WorkShiftSlot[] } = {};
  
  slots.forEach(slot => {
    if (!slotsByDay[slot.day_of_week]) {
      slotsByDay[slot.day_of_week] = [];
    }
    slotsByDay[slot.day_of_week].push(slot);
  });

  let totalHours = 0;

  // Verifica ogni giorno
  for (let day = 1; day <= 5; day++) {
    const daySlots = slotsByDay[day] || [];
    const isWorkingDay = day <= contract.giorni_settimanali;
    
    if (isWorkingDay) {
      if (daySlots.length === 0) {
        result.issues.push({
          type: 'ERROR',
          message: `Nessuna fascia definita per ${getDayName(day)}`,
          field: `day_${day}`,
        });
        result.isValid = false;
      } else {
        // Calcola ore totali del giorno
        const dayHours = daySlots.reduce((sum, slot) => sum + calculateSlotHours(slot), 0);
        totalHours += dayHours;

        // Verifica se le ore sono corrette
        const expectedDayHours = result.dailyHoursExpected;
        const tolerance = 0.5; // 30 minuti di tolleranza
        
        if (Math.abs(dayHours - expectedDayHours) > tolerance) {
          const diff = dayHours - expectedDayHours;
          result.issues.push({
            type: Math.abs(diff) > 2 ? 'ERROR' : 'WARNING',
            message: `${getDayName(day)}: ${dayHours.toFixed(2)}h invece di ${expectedDayHours.toFixed(2)}h (differenza: ${diff > 0 ? '+' : ''}${diff.toFixed(2)}h)`,
            field: `day_${day}`,
            expected: expectedDayHours,
            actual: dayHours,
          });
          if (Math.abs(diff) > 2) {
            result.isValid = false;
          }
        }
      }
    } else {
      // Giorno non lavorativo - verifica che non ci siano slot
      if (daySlots.length > 0) {
        result.issues.push({
          type: 'ERROR',
          message: `${getDayName(day)}: presenza di fasce per un giorno non lavorativo`,
          field: `day_${day}`,
        });
        result.isValid = false;
      }
    }
  }

  result.totalHoursFromSlots = totalHours;
  result.difference = totalHours - contract.ore_settimanali;

  // Verifica totale settimanale
  const weeklyTolerance = 1; // 1 ora di tolleranza
  if (Math.abs(result.difference) > weeklyTolerance) {
    result.issues.push({
      type: 'ERROR',
      message: `Totale settimanale: ${totalHours.toFixed(2)}h invece di ${contract.ore_settimanali}h (differenza: ${result.difference > 0 ? '+' : ''}${result.difference.toFixed(2)}h)`,
      expected: contract.ore_settimanali,
      actual: totalHours,
    });
    result.isValid = false;
  }

  return result;
}

/**
 * Converte numero giorno in nome
 */
function getDayName(day: number): string {
  const days = ['', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì'];
  return days[day] || '';
}

/**
 * Valida una singola fascia oraria
 */
export function validateSingleShift(slot: WorkShiftSlot): ShiftValidationIssue[] {
  const issues: ShiftValidationIssue[] = [];

  // Verifica che start_time < end_time
  const [startHour, startMin] = slot.start_time.split(':').map(Number);
  const [endHour, endMin] = slot.end_time.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  
  if (endMinutes <= startMinutes) {
    issues.push({
      type: 'ERROR',
      message: `Orario non valido: fine prima/inizio`,
      field: 'time',
    });
  }

  // Verifica durata minima (almeno 1 ora)
  const durationMinutes = endMinutes - startMinutes - slot.break_duration_minutes;
  if (durationMinutes < 60) {
    issues.push({
      type: 'ERROR',
      message: `Durata troppo breve (minimo 1 ora)`,
      field: 'duration',
    });
  }

  return issues;
}
